import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import MainLayout from "layouts/MainLayout";
import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  addWitnessProction,
  editWitnessProction,
  fetchWitnessProctionById,
} from "services/witnessProtectionService";
import { CrimeTypeForCountAndAppeal, WitnessProtection } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

const inputClass =
  "w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";

// Frontend-defined submission methods (value, default label).
const SUBMISSION_METHODS: { value: string; label: string }[] = [
  { value: "in_person", label: "In Person" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "letter", label: "Letter / Post" },
  { value: "online", label: "Online Portal" },
  { value: "representative", label: "Through Representative" },
];

const WitnessProtectionRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const isEdit = !!editId;

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [formState, setFormState] = useState<WitnessProtection>();
  const [step, setStep] = useState(0);

  // Edit mode: prefill the form with the existing registration.
  useEffect(() => {
    if (!editId) return;
    setFetching(true);
    fetchWitnessProctionById(editId)
      .then((data) => setFormState(data))
      .catch((err) =>
        showTooltip(
          err?.response?.data?.detail || errorHandlerOnForm(err),
          "error",
        ),
      )
      .finally(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const STEPS = [
    t("witness_protection.modals.request_witness_protection.step_applicant", {
      defaultValue: "Applicant",
    }),
    t("witness_protection.modals.request_witness_protection.step_case", {
      defaultValue: "Case & Submission",
    }),
    t("witness_protection.modals.request_witness_protection.step_documents", {
      defaultValue: "Documents",
    }),
  ];
  const isLastStep = step === STEPS.length - 1;

  const handleChange = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formState?.applicant_full_name)
      return t(
        "witness_protection.error_messages.applicant_full_name_required",
      );
    return "";
  };

  const handleNext = () => {
    if (step === 0 && !formState?.applicant_full_name) {
      showTooltip(validateForm(), "error");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  // Implicit form submission (Enter key, etc.) must NEVER save the registration
  // — it can only advance the wizard. The actual save happens solely via the
  // explicit "Submit" button on the last step (doSubmit).
  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isLastStep) handleNext();
  };

  const doSubmit = () => {
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      setStep(0);
      return;
    }

    // Send only the editable registration fields. Files are included only when
    // newly selected, so an edit that doesn't re-upload keeps the existing file.
    const f: any = formState || {};
    const payload: any = {
      crime_type: f.crime_type,
      applicant_full_name: f.applicant_full_name,
      applicant_region: f.applicant_region,
      applicant_city: f.applicant_city,
      applicant_woreda: f.applicant_woreda,
      applicant_phone_number: f.applicant_phone_number,
      applicant_id_card_number: f.applicant_id_card_number,
      submission_method: f.submission_method,
      court_registration_number: f.court_registration_number,
      protection_request_reason: f.protection_request_reason,
      remark: f.remark,
      admin_unit: loginData?.admin_unit?.toString() ?? "",
    };
    if (f.id_card_attachment instanceof File)
      payload.id_card_attachment = f.id_card_attachment;
    if (f.supplementary_document instanceof File)
      payload.supplementary_document = f.supplementary_document;
    if (f.petition_document instanceof File)
      payload.petition_document = f.petition_document;

    setLoading(true);
    const request = isEdit
      ? editWitnessProction({ ...payload, id: editId })
      : addWitnessProction(payload);

    request
      .then(() => {
        showTooltip(
          t("witness_protection.success_messages.successfully_requested"),
          "success",
        );
        navigate(isEdit ? `/witness-protection/${editId}` : "/witness-protection");
        setLoading(false);
      })
      .catch((err) => {
        showTooltip(
          err?.response?.data?.detail || errorHandlerOnForm(err),
          "error",
        );
        setLoading(false);
      });
  };

  if (fetching) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="w-3/4 pb-10">
        {/* Header */}
        <div className="relative overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-red-600 to-rose-500" />
          <div className="p-6 pl-7 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-600 to-rose-500 flex items-center justify-center shadow-sm shrink-0">
              <span className="text-2xl">🛡️</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEdit
                  ? t(
                      "witness_protection.modals.request_witness_protection.edit_title",
                    )
                  : t(
                      "witness_protection.modals.request_witness_protection.new_title",
                    )}
              </h1>
              <p className="text-sm text-gray-500">
                {t(
                  "witness_protection.modals.request_witness_protection.form_description",
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col min-h-[70vh]">
          {/* Stepper */}
          <div className="border-b border-gray-200 px-6 py-5">
            <div className="flex items-center">
              {STEPS.map((label, i) => (
                <Fragment key={i}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                        i < step
                          ? "bg-green-500 text-white"
                          : i === step
                            ? "bg-red-600 text-white"
                            : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {i < step ? "✓" : i + 1}
                    </div>
                    <span
                      className={`text-sm font-medium hidden sm:inline ${
                        i === step ? "text-gray-900" : "text-gray-500"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-3 ${
                        i < step ? "bg-green-500" : "bg-gray-200"
                      }`}
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          <form
            className="p-6 space-y-4 flex-1 flex flex-col"
            onSubmit={handleFormSubmit}
          >
            {/* Step 1: Applicant Information */}
            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.applicant_full_name",
                    )}
                    <span className="text-red-500"> *</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    name="applicant_full_name"
                    value={formState?.applicant_full_name ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.crime_type",
                    )}
                  </label>
                  <select
                    className={`${inputClass} capitalize`}
                    name="crime_type"
                    value={formState?.crime_type ?? ""}
                    onChange={handleChange}
                  >
                    <option value={""}>
                      {t(
                        "witness_protection.modals.request_witness_protection.select_crime_type",
                      )}
                    </option>
                    {Object.keys(CrimeTypeForCountAndAppeal).map((key) => (
                      <option key={key} value={key}>
                        {
                          CrimeTypeForCountAndAppeal[
                            key as keyof typeof CrimeTypeForCountAndAppeal
                          ]
                        }
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.applicant_region",
                    )}
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    name="applicant_region"
                    value={formState?.applicant_region ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.applicant_city",
                    )}
                  </label>
                  <input
                    className={inputClass}
                    name="applicant_city"
                    value={formState?.applicant_city ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.applicant_woreda",
                    )}
                  </label>
                  <input
                    className={inputClass}
                    name="applicant_woreda"
                    value={formState?.applicant_woreda ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.applicant_phone_number",
                    )}
                  </label>
                  <input
                    type="phone"
                    className={inputClass}
                    name="applicant_phone_number"
                    value={formState?.applicant_phone_number ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.applicant_id_card",
                    )}
                  </label>
                  <input
                    className={inputClass}
                    name="applicant_id_card_number"
                    value={formState?.applicant_id_card_number ?? ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            {/* Step 2: Case & Submission */}
            {step === 1 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.submission_method",
                    )}
                  </label>
                  <select
                    className={inputClass}
                    name="submission_method"
                    value={formState?.submission_method ?? ""}
                    onChange={handleChange}
                  >
                    <option value="">
                      {t(
                        "witness_protection.modals.request_witness_protection.select_submission_method",
                        { defaultValue: "Select Submission Method" },
                      )}
                    </option>
                    {SUBMISSION_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {t(
                          `witness_protection.submission_method_options.${m.value}`,
                          { defaultValue: m.label },
                        )}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.court_registration_number",
                    )}
                  </label>
                  <input
                    className={inputClass}
                    name="court_registration_number"
                    value={formState?.court_registration_number ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>
                    {t(
                      "witness_protection.modals.request_witness_protection.protection_request_reason",
                    )}
                  </label>
                  <textarea
                    rows={4}
                    className={inputClass}
                    name="protection_request_reason"
                    value={formState?.protection_request_reason ?? ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>
                    {t("witness_protection.modals.request_witness_protection.remark")}
                  </label>
                  <textarea
                    rows={4}
                    className={inputClass}
                    name="remark"
                    value={formState?.remark ?? ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Documents */}
            {step === 2 && (
              <div className="grid grid-cols-1 gap-4">
                {isEdit && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {t(
                      "witness_protection.modals.request_witness_protection.edit_documents_note",
                      {
                        defaultValue:
                          "Existing attachments can't be previewed here. Upload a file to replace it, or leave it empty to keep the current one.",
                      },
                    )}
                  </div>
                )}
                <FileUpload
                  label={t(
                    "witness_protection.modals.request_witness_protection.id_card_attachment",
                  )}
                  fileTypes="image/*"
                  info={t(
                    "witness_protection.modals.request_witness_protection.upload_images_info",
                  )}
                  required={!isEdit}
                  onChange={(file) =>
                    setFormState((prev) => ({
                      ...prev,
                      id_card_attachment: file,
                    }))
                  }
                />
                <FileUpload
                  label={t(
                    "witness_protection.modals.request_witness_protection.supplementary_document",
                  )}
                  fileTypes="*"
                  info={t(
                    "witness_protection.modals.request_witness_protection.upload_files_info",
                  )}
                  required={!isEdit}
                  onChange={(file) =>
                    setFormState((prev) => ({
                      ...prev,
                      supplementary_document: file,
                    }))
                  }
                />
                <FileUpload
                  label={t(
                    "witness_protection.modals.request_witness_protection.petition_document",
                  )}
                  fileTypes="*"
                  info={t(
                    "witness_protection.modals.request_witness_protection.upload_files_info",
                  )}
                  required={!isEdit}
                  onChange={(file) =>
                    setFormState((prev) => ({
                      ...prev,
                      petition_document: file,
                    }))
                  }
                />
              </div>
            )}

            {/* Navigation */}
            <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() =>
                  step === 0
                    ? navigate(
                        isEdit
                          ? `/witness-protection/${editId}`
                          : "/witness-protection",
                      )
                    : setStep((s) => Math.max(s - 1, 0))
                }
                className="px-5 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {step === 0
                  ? t("cancel", { defaultValue: "Cancel" })
                  : t("back", { defaultValue: "Back" })}
              </button>

              {isLastStep ? (
                <button
                  type="button"
                  onClick={doSubmit}
                  className={`px-8 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      {LoadingIcon}
                      {t("loading")}
                    </span>
                  ) : isEdit ? (
                    t(
                      "witness_protection.modals.request_witness_protection.edit_request",
                    )
                  ) : (
                    t(
                      "witness_protection.modals.request_witness_protection.submit_request",
                    )
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-8 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  {t("next", { defaultValue: "Next" })}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default WitnessProtectionRequest;
